import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { Location } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

import { authApi, vertaalApiFout } from "@/lib/api";
import { useAuthStore } from "@/lib/auth-store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function TwoFactorPagina() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // Doorgegeven vanaf Login: de pagina waar de bezoeker oorspronkelijk
  // heen wilde (bookmark, deeplink of pushmelding).
  const vandaan = (location.state as { from?: Location } | null)?.from;
  const pendingToken = useAuthStore((s) => s.tweefaPendingToken);
  const accessToken = useAuthStore((s) => s.accessToken);
  const setTokens = useAuthStore((s) => s.setTokens);
  const setPending2FA = useAuthStore((s) => s.setPending2FA);

  const [code, setCode] = useState("");
  const [bezig, setBezig] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Alleen terug naar /login als er geen enkele auth-context is.
    // Na succesvol 2FA wordt pendingToken gewist *en* accessToken gezet —
    // dan mogen we niet terugsturen, valideerCode navigeert naar /.
    if (!pendingToken && !accessToken) {
      navigate("/login", { replace: true });
      return;
    }
    // Multi-tab (27-07): is er al een sessie — omdat een ander tabblad
    // de login afrondde (tokens via storage-sync) of dit tabblad zelf —
    // dan direct door naar het portaal.
    if (accessToken) {
      navigate(vandaan ?? "/", { replace: true });
      return;
    }
    inputRef.current?.focus();
  }, [pendingToken, accessToken, navigate, vandaan]);

  useEffect(() => {
    if (code.length === 6 && !bezig) {
      void valideerCode(code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  async function valideerCode(ingegevenCode: string) {
    if (!pendingToken) return;
    setBezig(true);
    try {
      const resp = await authApi.valideer2FA({
        tweefa_pending_token: pendingToken,
        code: ingegevenCode,
      });
      setTokens(resp.access_token, resp.refresh_token);
      setPending2FA(null);
      navigate(vandaan ?? "/", { replace: true });
    } catch (fout) {
      toast.error(vertaalApiFout(fout, t("auth.login_2fa_mislukt")));
      setCode("");
      inputRef.current?.focus();
    } finally {
      setBezig(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (code.length === 6) void valideerCode(code);
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6">
          <div
            className="h-14 w-14 rounded-2xl bg-brand-cyan text-white grid place-items-center"
            aria-hidden
          >
            <KeyRound className="h-6 w-6" />
          </div>
        </div>
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-tight">
            {t("auth.twee_factor_titel")}
          </h1>
          <p className="text-[14px] text-muted-foreground mt-1">
            {t("auth.twee_factor_uitleg")}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-card border rounded-xl shadow-sm p-6 space-y-4"
          noValidate
        >
          <div className="space-y-1.5">
            <Label htmlFor="code">{t("auth.twee_factor_code")}</Label>
            <Input
              ref={inputRef}
              id="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              className="text-center text-xl tracking-[0.5em] font-mono"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/gu, ""))}
              disabled={bezig}
            />
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={code.length !== 6 || bezig}
          >
            {bezig && <Loader2 className="h-4 w-4 animate-spin" />}
            {t("algemeen.bevestigen")}
          </Button>
        </form>

        <div className="text-center mt-6">
          <button
            type="button"
            onClick={() => {
              setPending2FA(null);
              navigate("/login", { replace: true });
            }}
            className="text-[13px] text-muted-foreground hover:text-foreground"
          >
            ← {t("algemeen.terug")}
          </button>
        </div>
      </div>
    </div>
  );
}
